/**
 * Amberden — the bag, kept honest.
 *
 * Batch 0 of the navigation refactor. Two jobs, both caused by the fact that
 * adding to the cart never reloads the page:
 *
 *   1. The paper header's Bag count is server-rendered Liquid. A quick-look add
 *      on /pages/studio left it reading the count from page load until the
 *      visitor reloaded — the one number on the page a buyer checks to know the
 *      add worked.
 *   2. One Bag control. The product header's Bag opened the drawer while the
 *      paper header's navigated to /cart — same word, same icon, two
 *      behaviours. The paper one now opens the drawer too. (The old
 *      reveal-a-View-bag-link job is gone: auto_open_cart_drawer is on, so an
 *      add opens the drawer everywhere and confirms itself.)
 *
 * It listens to the theme's own cart event rather than patching fetch, so it
 * stays correct if Horizon changes how it posts. Every failure is silent: the
 * count keeps its server value and View bag stays hidden, which is the state
 * the page already shipped in.
 */

import { StandardEvents } from '@shopify/events';

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function paint(n) {
  document.querySelectorAll('[data-amb-bag-count]').forEach((el) => {
    el.textContent = n > 0 ? `(${n})` : '';
    el.hidden = n === 0;
  });
}

async function refreshCount() {
  if (!document.querySelector('[data-amb-bag-count]')) return;
  try {
    const res = await fetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return;
    const cart = await res.json();
    if (typeof cart.item_count === 'number') paint(cart.item_count);
  } catch (e) {
    /* leave the server-rendered count alone */
  }
}

/* Horizon dispatches cartLinesUpdate *optimistically* — before the change has
 * reached the server — and hands the real result over on event.promise. The
 * first version of this file fetched /cart.js the moment the event landed, so
 * it always read the count from *before* the change: bump a line to 2 and the
 * header still said (1); empty the bag and it said (2). This mirrors what
 * Horizon's own cart-icon.js does, and only falls back to a fetch if the
 * promise is missing or rejects.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = event && event.promise;
  if (!promise || typeof promise.then !== 'function') {
    refreshCount();
    return;
  }
  promise
    .then((result) => {
      const cart = result && result.cart;
      const detail = result && result.detail;
      const n =
        cart && typeof cart.totalQuantity === 'number'
          ? cart.totalQuantity
          : detail && typeof detail.itemCount === 'number'
            ? detail.itemCount
            : null;
      if (n === null) refreshCount();
      else paint(n);
    })
    .catch(() => refreshCount());
});

// Coming back through the bfcache replays a stale document; re-read the cart.
window.addEventListener('pageshow', (e) => {
  if (e.persisted) refreshCount();
});

/* ---- One Bag control ----------------------------------------------------
 * The product header's Bag opens the drawer; the paper header's used to
 * navigate to /cart. Same word, same icon, two behaviours depending on which
 * room you were in. This makes the paper one open the drawer too.
 *
 * It stays an anchor: with JavaScript off, or on the cart template where no
 * drawer is rendered, the href still takes you to the bag page. Modified
 * clicks (new tab, middle click) are left alone.
 *
 * Open it through <theme-drawer>'s own open(), NOT by reaching past it to the
 * inner <dialog> and calling showModal(). That shortcut shipped on 2026-09-12
 * and broke the drawer on a laptop: theme-drawer presents as a MODAL only
 * below its own breakpoint and as a plain side panel above it, so forcing
 * showModal() on a desktop laid an inert full-page backdrop over the site.
 * Worse, the component's state never changed — the dialog was open while
 * theme-drawer still had no [open] attribute — so close() was a no-op and the
 * click-outside and keydown handlers that showDialog() registers were never
 * added. The × did nothing, clicking away did nothing, and only Escape (native
 * <dialog> behaviour) got you out. A phone hid it: there the drawer is
 * supposed to be modal, so the wrong call happened to look right.
 */
document.addEventListener('click', function (e) {
  var bag = e.target && e.target.closest ? e.target.closest('[data-amb-bag]') : null;
  if (!bag) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

  // On the bag page itself Horizon renders no drawer, and following the href
  // would reload the page the visitor is already looking at.
  if (bag.getAttribute('aria-current') === 'page') {
    e.preventDefault();
    return;
  }

  var drawer = document.getElementById('cart-drawer');
  if (!drawer) return; // no drawer here (the cart template): follow the link

  // The element can be in the DOM before its module has run. Upgrading is
  // synchronous and idempotent; if it still isn't ready, fall through to the
  // href rather than swallowing the click.
  if (typeof drawer.open !== 'function' && window.customElements) {
    try { window.customElements.upgrade(drawer); } catch (err) { /* fall through */ }
  }
  if (typeof drawer.open !== 'function') return;

  e.preventDefault();
  if (!drawer.isOpen) drawer.open();
});
